import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import {
  SpecialRules,
  HAND_RANKINGS_INFO,
  SPECIAL_RULES_INFO,
  HandRankingInfo,
  SpecialRuleInfo,
  getActiveSpecialRules,
} from '../types/GameState';

// ============================================================================
// TYPES
// ============================================================================

interface TableRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  specialRules?: SpecialRules;
  tableName?: string;
  smallBlind?: number;
  bigBlind?: number;
}

// ============================================================================
// CARD DISPLAY COMPONENT
// ============================================================================

const CardDisplay: React.FC<{ card: string; small?: boolean }> = ({ card, small }) => {
  // Parse card string like "A" or "10"

  const isRed = card.includes('') || card.includes('');

  
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: small ? '28px' : '36px',
        height: small ? '40px' : '48px',
        background: '#fff',
        borderRadius: '4px',
        color: isRed ? '#dc2626' : '#1a1a1a',
        fontWeight: 'bold',
        fontSize: small ? '12px' : '14px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        margin: '0 2px',
      }}
    >
      {card}
    </span>
  );
};

// ============================================================================
// HAND RANKING ROW COMPONENT
// ============================================================================

const HandRankingRow: React.FC<{ hand: HandRankingInfo }> = ({ hand }) => {
  // Split example into individual cards
  const cards = hand.example.split(' ');
  
  return (
    <Box
      p={4}
      bg="rgba(255, 255, 255, 0.03)"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.700"
      _hover={{ borderColor: 'cyan.600', bg: 'rgba(0, 217, 217, 0.05)' }}
      transition="all 0.2s"
    >
      <HStack justify="space-between" mb={2}>
        <HStack>
          <Badge
            colorScheme={hand.rank >= 8 ? 'yellow' : hand.rank >= 5 ? 'cyan' : 'gray'}
            fontSize="md"
            px={2}
            py={1}
          >
            #{hand.rank}
          </Badge>
          <Text fontWeight="bold" color="white" fontSize="lg">
            {hand.name}
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.500">
          {hand.probability}
        </Text>
      </HStack>
      
      <Text fontSize="sm" color="gray.400" mb={3}>
        {hand.description}
      </Text>
      
      <HStack spacing={1} flexWrap="wrap">
        {cards.map((card, idx) => (
          <CardDisplay key={idx} card={card} small />
        ))}
      </HStack>
    </Box>
  );
};

// ============================================================================
// SPECIAL RULE ROW COMPONENT
// ============================================================================

const SpecialRuleRow: React.FC<{ 
  rule: SpecialRuleInfo; 
  isActive: boolean;
  config?: { bounty?: number; ante?: number; straddleMax?: number };
}> = ({ rule, isActive, config }) => {
  return (
    <Box
      p={4}
      bg={isActive ? 'rgba(0, 217, 217, 0.1)' : 'rgba(255, 255, 255, 0.03)'}
      borderRadius="lg"
      border="1px solid"
      borderColor={isActive ? 'cyan.500' : 'gray.700'}
      opacity={isActive ? 1 : 0.6}
    >
      <HStack justify="space-between" mb={2}>
        <HStack>
          <Text fontSize="2xl">{rule.icon}</Text>
          <Text fontWeight="bold" color={isActive ? 'cyan.400' : 'white'} fontSize="lg">
            {rule.name}
          </Text>
        </HStack>
        <Badge colorScheme={isActive ? 'green' : 'gray'}>
          {isActive ? 'ACTIVE' : 'OFF'}
        </Badge>
      </HStack>
      
      <Text fontSize="sm" color="gray.400" mb={2}>
        {rule.description}
      </Text>
      
      {isActive && (
        <Box
          mt={3}
          p={3}
          bg="rgba(0, 0, 0, 0.2)"
          borderRadius="md"
          fontSize="sm"
          color="gray.300"
        >
          {rule.detailedExplanation}
          
          {/* Show config values if available */}
          {config && (
            <Box mt={2} pt={2} borderTop="1px solid" borderColor="gray.600">
              {rule.id === 'sevenTwoGame' && config.bounty && (
                <Text color="cyan.400">
                   Bounty: {config.bounty}x Big Blind per player

                </Text>
              )}
              {rule.id === 'bombPotEnabled' && config.ante && (
                <Text color="cyan.400">
                   Ante: {config.ante}x Big Blind per player

                </Text>
              )}
              {rule.id === 'straddleEnabled' && config.straddleMax && (
                <Text color="cyan.400">
                  ⬆ Max Straddle: {config.straddleMax}x Big Blind

                </Text>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const TableRulesModal: React.FC<TableRulesModalProps> = ({
  isOpen,
  onClose,
  specialRules,
  tableName,
  smallBlind,
  bigBlind,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  
  const activeRules = specialRules ? getActiveSpecialRules(specialRules) : [];
  const hasActiveRules = activeRules.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="rgba(0, 0, 0, 0.85)" />
      <ModalContent
        bg="linear-gradient(135deg, #1A2E3B 0%, #0D1B2A 100%)"
        borderColor="cyan.600"
        borderWidth="1px"
        borderRadius="xl"
        maxH="85vh"
      >
        <ModalHeader color="cyan.400" borderBottom="1px solid" borderColor="gray.700">
          <HStack>
            <Icon as={InfoIcon} />
            <Text> Table Rules & Info</Text>

          </HStack>
          {tableName && (
            <Text fontSize="sm" color="gray.400" fontWeight="normal" mt={1}>
              {tableName} {smallBlind && bigBlind && `• ${smallBlind}/${bigBlind}`}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton color="gray.400" />
        
        <ModalBody pb={6}>
          <Tabs
            index={tabIndex}
            onChange={setTabIndex}
            colorScheme="cyan"
            variant="soft-rounded"
          >
            <TabList mb={4} flexWrap="wrap" gap={2}>
              <Tab
                _selected={{ bg: 'cyan.600', color: 'white' }}
                color="gray.400"
              >
                🃏 Hand Rankings
              </Tab>
              <Tab
                _selected={{ bg: 'cyan.600', color: 'white' }}
                color="gray.400"
              >
                 Special Rules

                {hasActiveRules && (
                  <Badge ml={2} colorScheme="green" fontSize="xs">
                    {activeRules.length}
                  </Badge>
                )}
              </Tab>
              <Tab
                _selected={{ bg: 'cyan.600', color: 'white' }}
                color="gray.400"
              >
                 Poker Basics

              </Tab>
            </TabList>

            <TabPanels>
              {/* ===== HAND RANKINGS TAB ===== */}
              <TabPanel p={0}>
                <Text color="gray.400" mb={4} fontSize="sm">
                  Poker hands ranked from strongest (#10) to weakest (#1).
                  In Texas Hold'em, you make the best 5-card hand from your 2 hole cards and 5 community cards.
                </Text>
                <VStack spacing={3} align="stretch">
                  {HAND_RANKINGS_INFO.map((hand) => (
                    <HandRankingRow key={hand.rank} hand={hand} />
                  ))}
                </VStack>
              </TabPanel>

              {/* ===== SPECIAL RULES TAB ===== */}
              <TabPanel p={0}>
                {hasActiveRules ? (
                  <>
                    <Box
                      p={4}
                      mb={4}
                      bg="rgba(0, 217, 217, 0.1)"
                      borderRadius="lg"
                      border="1px solid"
                      borderColor="cyan.500"
                    >
                      <HStack mb={2}>
                        <Text fontSize="lg"></Text>

                        <Text fontWeight="bold" color="cyan.400">
                          Active Special Rules at This Table
                        </Text>
                      </HStack>
                      <HStack spacing={2} flexWrap="wrap">
                        {activeRules.map((rule) => (
                          <Badge
                            key={rule.id}
                            colorScheme="cyan"
                            fontSize="sm"
                            px={3}
                            py={1}
                          >
                            {rule.icon} {rule.shortName}
                          </Badge>
                        ))}
                      </HStack>
                    </Box>
                    <Text color="gray.400" mb={4} fontSize="sm">
                      These rules were set when the table was created and cannot be changed during the game.
                    </Text>
                  </>
                ) : (
                  <Box
                    p={4}
                    mb={4}
                    bg="rgba(255, 255, 255, 0.03)"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.700"
                  >
                    <Text color="gray.400" textAlign="center">
                       This table is using standard Texas Hold'em rules with no special variations.

                    </Text>
                  </Box>
                )}
                
                <VStack spacing={3} align="stretch">
                  {SPECIAL_RULES_INFO.map((rule) => {
                    const isActive = specialRules?.[rule.id as keyof SpecialRules] === true;
                    const config = specialRules ? {
                      bounty: specialRules.sevenTwoBounty,
                      ante: specialRules.bombPotAnte,
                      straddleMax: specialRules.straddleMax,
                    } : undefined;
                    
                    return (
                      <SpecialRuleRow
                        key={rule.id}
                        rule={rule}
                        isActive={isActive}
                        config={isActive ? config : undefined}
                      />
                    );
                  })}
                </VStack>
              </TabPanel>

              {/* ===== POKER BASICS TAB ===== */}
              <TabPanel p={0}>
                <VStack spacing={4} align="stretch">
                  {/* Game Flow */}
                  <Box
                    p={4}
                    bg="rgba(255, 255, 255, 0.03)"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.700"
                  >
                    <Text fontWeight="bold" color="cyan.400" mb={2}>
                       Game Flow

                    </Text>
                    <VStack align="start" spacing={2} fontSize="sm" color="gray.300">
                      <Text>1. <strong>Blinds Posted</strong> - Small blind and big blind are forced bets</Text>
                      <Text>2. <strong>Hole Cards</strong> - Each player gets 2 private cards</Text>
                      <Text>3. <strong>Preflop</strong> - First betting round</Text>
                      <Text>4. <strong>Flop</strong> - 3 community cards revealed, betting</Text>
                      <Text>5. <strong>Turn</strong> - 4th community card, betting</Text>
                      <Text>6. <strong>River</strong> - 5th community card, final betting</Text>
                      <Text>7. <strong>Showdown</strong> - Best 5-card hand wins!</Text>
                    </VStack>
                  </Box>

                  {/* Actions */}
                  <Box
                    p={4}
                    bg="rgba(255, 255, 255, 0.03)"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.700"
                  >
                    <Text fontWeight="bold" color="cyan.400" mb={2}>
                       Actions

                    </Text>
                    <VStack align="start" spacing={2} fontSize="sm" color="gray.300">
                      <HStack>
                        <Badge colorScheme="gray">Check</Badge>
                        <Text>Pass action (when no bet to call)</Text>
                      </HStack>
                      <HStack>
                        <Badge colorScheme="blue">Call</Badge>
                        <Text>Match the current bet</Text>
                      </HStack>
                      <HStack>
                        <Badge colorScheme="green">Raise</Badge>
                        <Text>Increase the bet amount</Text>
                      </HStack>
                      <HStack>
                        <Badge colorScheme="red">Fold</Badge>
                        <Text>Give up your hand and any bets</Text>
                      </HStack>
                      <HStack>
                        <Badge colorScheme="orange">All-In</Badge>
                        <Text>Bet all your chips</Text>
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Positions */}
                  <Box
                    p={4}
                    bg="rgba(255, 255, 255, 0.03)"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.700"
                  >
                    <Text fontWeight="bold" color="cyan.400" mb={2}>
                       Table Positions

                    </Text>
                    <VStack align="start" spacing={2} fontSize="sm" color="gray.300">
                      <HStack>
                        <Badge colorScheme="yellow">BTN</Badge>
                        <Text>Dealer Button - Best position, acts last</Text>
                      </HStack>
                      <HStack>
                        <Badge colorScheme="cyan">SB</Badge>
                        <Text>Small Blind - Half the big blind, acts first</Text>
                      </HStack>
                      <HStack>
                        <Badge colorScheme="cyan">BB</Badge>
                        <Text>Big Blind - Full blind, acts second</Text>
                      </HStack>
                      <HStack>
                        <Badge colorScheme="gray">UTG</Badge>
                        <Text>Under the Gun - First to act preflop</Text>
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Rake Info */}
                  <Box
                    p={4}
                    bg="rgba(255, 152, 0, 0.05)"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="orange.700"
                  >
                    <Text fontWeight="bold" color="orange.400" mb={2}>
                       Rake & Fees

                    </Text>
                    <VStack align="start" spacing={2} fontSize="sm" color="gray.300">
                      <HStack justify="space-between" w="100%">
                        <Text fontWeight="medium">Cash Games:</Text>
                        <Text color="orange.300">5% of pot (max $5)</Text>
                      </HStack>
                      <HStack justify="space-between" w="100%">
                        <Text fontWeight="medium">Tournaments:</Text>
                        <Text color="orange.300">10% of buy-in</Text>
                      </HStack>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Rake is the small fee collected to support the platform.
                      </Text>
                    </VStack>
                  </Box>

                  {/* Tips */}
                  <Box
                    p={4}
                    bg="rgba(0, 217, 217, 0.05)"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="cyan.700"
                  >
                    <Text fontWeight="bold" color="cyan.400" mb={2}>
                       Quick Tips

                    </Text>
                    <VStack align="start" spacing={2} fontSize="sm" color="gray.300">
                      <Text>• Play tight early - fold weak hands</Text>
                      <Text>• Position matters - late position is powerful</Text>
                      <Text>• Don't chase draws without the right odds</Text>
                      <Text>• Pay attention to opponents' betting patterns</Text>
                      <Text>• Manage your bankroll - don't risk too much at once</Text>
                    </VStack>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TableRulesModal;